import { useState, useEffect } from 'react';

interface ElapsedTimeOptions {
  dischargeStatus?: string;
  dischargeCompletionTime?: string | Date | null;
}

/**
 * Hook to calculate total elapsed time from MD (Marked for Discharge) completion time.
 * The timer starts when the patient is marked for discharge (MD activity completed).
 * @param mdCompletionTime - The completion time of the MD activity (when patient was marked for discharge)
 * @param options - Additional options for handling discharged patients
 */
export const useTotalElapsedTime = (
  mdCompletionTime?: Date | string | null,
  options?: ElapsedTimeOptions
) => {
  const [elapsedTime, setElapsedTime] = useState("00:00:00");
  const isDischarged = options?.dischargeStatus === "discharged";

  useEffect(() => {
    // If no MD completion time, patient hasn't been marked for discharge yet
    if (!mdCompletionTime) {
      setElapsedTime("00:00:00");
      return;
    }

    const calculateTime = () => {
      try {
        const mdTime = new Date(mdCompletionTime);
        
        // If patient is discharged, use physical discharge completion time as end time
        let endTime: Date;
        if (isDischarged && options?.dischargeCompletionTime) {
          endTime = new Date(options.dischargeCompletionTime);
        } else {
          endTime = new Date(); // Current time
        }
        
        const diffMs = endTime.getTime() - mdTime.getTime();
        
        // Handle negative values (shouldn't happen, but just in case)
        if (diffMs < 0) {
          return "00:00:00";
        }
        
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
        
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      } catch {
        return "00:00:00";
      }
    };

    // Calculate immediately
    setElapsedTime(calculateTime());

    // If discharged, don't start interval (frozen time)
    if (isDischarged) {
      return;
    }

    // Update every second for non-discharged patients
    const interval = setInterval(() => {
      setElapsedTime(calculateTime());
    }, 1000);

    return () => clearInterval(interval);
  }, [mdCompletionTime, isDischarged, options?.dischargeCompletionTime]);

  return elapsedTime;
};
